import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../store/hook';
import { selectLmsLists } from '../../../store/features/lmsSlice';
import { useEffect, useState } from 'react';
import TextWinds from '../../../components/common/TextWinds';
import {
  getClassBookList,
  getLastTeachingBook,
} from '../../../api/utils/lms/take-class/api';
import { openAlert } from '../../../store/features/modalSlice';
import { getErrorMessages } from '../../../api/utils/util-func';
import {
  ClassBookItem,
  LastTeachingBookItem,
} from '../../../constants/types/lms/take-class/type';
import { LoadingSpinner } from '../../../components/Loading/LoadingSpinner';
import BookLessonPanel from '../../../components/lms/take-class/BookLessonPanel';
import BookListPanel from '../../../components/lms/take-class/BookListPanel';
import TopButton from '../../../components/button/TopButton';
import LmsGuideButton from '../../../components/lms/LmsGuideButton';
import { LmsLinkItemKey } from '../../../constants/types/lms/type';

/**
 * 개편 LMS (교사) - 수업하기 페이지
 */
const TakeClass = () => {
  const { classId } = useParams();

  const lmsLists = useAppSelector(selectLmsLists);

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [bookList, setBookList] = useState<ClassBookItem[]>([]);
  const [lastTeachingBook, setLastTeachingBook] =
    useState<LastTeachingBookItem | null>(null);

  const [selectedBookCode, setSelectedBookCode] = useState<string | null>(null);

  useEffect(() => {
    if (!lmsLists.course || !classId) return;

    const fetchClassBookList = async () => {
      try {
        setIsLoading(true);

        const response = await getClassBookList({
          classId,
        });

        setBookList(response.books ?? []);

        const lastTeachingBook = await getLastTeachingBook({ classId });

        setLastTeachingBook(lastTeachingBook ?? null);
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchClassBookList();
  }, [lmsLists.course, classId, dispatch, selectedBookCode]);

  const handleBack = () => {
    setSelectedBookCode(null);
  };

  const handleBookClick = (bookCode: string) => {
    setSelectedBookCode(bookCode);
  };

  if (isLoading) {
    return <LoadingSpinner message="데이터 조회중" />;
  }

  return (
    <div className="flex w-full flex-col pt-6 xl:px-5">
      <header className="flex flex-col gap-3 px-5 xl:flex-row xl:items-center xl:justify-between xl:px-0">
        <div className="min-w-0">
          <TextWinds type="title_h2" xlType="title_h3" className="mb-[2px]">
            수업자료 보기
          </TextWinds>

          <TextWinds type="content_body3">
            수업에 필요한 자료를 선택한 후 새창에서 자료를 이용하세요.
          </TextWinds>
        </div>

        <LmsGuideButton itemKey={LmsLinkItemKey.MATERIAL_VIEW} />
      </header>

      <div className="my-5 gap-4 px-3 xl:px-0">
        {selectedBookCode ? (
          <BookLessonPanel
            selectedBookCode={selectedBookCode}
            onBack={handleBack}
          />
        ) : (
          <BookListPanel
            lastTeachingBook={lastTeachingBook}
            bookList={bookList}
            onBookClick={handleBookClick}
          />
        )}
      </div>

      <TopButton />
    </div>
  );
};

export default TakeClass;
